import { useQuery } from '@tanstack/react-query';
import { getMessage, type EmailAttachment } from '@/api/email';

function downloadHref(att: EmailAttachment): string | null {
  // Upstream exposes attachment metadata + a private storageKey but no bytes,
  // so content_base64 is effectively always absent → no inline download.
  if (!att.content_base64) return null;
  return `data:${att.mime_type || 'application/octet-stream'};base64,${att.content_base64}`;
}

function formatBytes(n?: number | null): string {
  if (!n || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// True if the HTML carries anything visible (text or an image), so we don't
// render a blank iframe for empty emails like `<div><br></div>`.
function htmlHasContent(html: string): boolean {
  const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
  return text.length > 0 || /<img\b/i.test(html);
}

// Inline `cid:` images reference the message's own attachments, which we can't
// fetch (no attachment-bytes endpoint upstream). Replace them with a labeled
// placeholder instead of a broken-image icon.
function replaceCidImages(html: string): string {
  return html.replace(
    /<img\b[^>]*\bsrc=["']cid:[^"']*["'][^>]*>/gi,
    '<span style="display:inline-block;padding:2px 8px;border:1px dashed #bbb;border-radius:6px;color:#666;font:13px system-ui">📎 inline image (not available)</span>'
  );
}

// Wrap the email fragment in a full document with UTF-8 and a strict CSP. The
// iframe is already `sandbox=""` (scripts dead); the CSP additionally blocks
// scripts, frames, objects and network fetches while allowing images and the
// inline styles emails rely on. Remote images load (standard webmail behavior).
function buildSrcDoc(html: string): string {
  const csp =
    "default-src 'none'; img-src data: https: http:; style-src 'unsafe-inline'; font-src data: https:";
  const styles =
    'body{font-family:system-ui,-apple-system,sans-serif;margin:0;padding:12px;color:#111;background:#fff;word-break:break-word}img{max-width:100%;height:auto}a{color:#2563eb}';
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}"><style>${styles}</style></head><body>${replaceCidImages(html)}</body></html>`;
}

export default function MessageView({ id, onClose }: { id: string; onClose: () => void }) {
  const msg = useQuery({ queryKey: ['email', 'message', id], queryFn: () => getMessage(id) });

  if (msg.isLoading) return <div className="p-4 text-content-subtle">Loading…</div>;
  if (msg.isError || !msg.data) return <div className="p-4 text-danger">Failed to load message.</div>;

  const m = msg.data;
  const showHtml = !!m.html_body && htmlHasContent(m.html_body);
  const showText = !showHtml && !!m.text_body && m.text_body.trim().length > 0;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between border-b border-border p-4">
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold text-content">{m.subject || '(no subject)'}</h2>
          <p className="truncate text-sm text-content-muted">From: {m.from || '—'}</p>
          {m.to && <p className="truncate text-sm text-content-muted">To: {m.to}</p>}
        </div>
        <button
          onClick={onClose}
          className="ml-2 shrink-0 rounded-full p-1 text-content-subtle hover:bg-surface-muted hover:text-content-muted"
          aria-label="Close"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-4">
        {showHtml ? (
          <iframe
            title="email-body"
            sandbox=""
            srcDoc={buildSrcDoc(m.html_body!)}
            className="min-h-0 w-full flex-1 rounded-lg border border-border bg-white"
          />
        ) : showText ? (
          <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap break-words text-sm text-content">
            {m.text_body}
          </pre>
        ) : (
          <p className="text-sm italic text-content-subtle">(empty message)</p>
        )}

        {(m.attachments?.length ?? 0) > 0 && (
          <div className="mt-4 shrink-0">
            <h3 className="mb-2 text-sm font-medium text-content-muted">Attachments</h3>
            <ul className="space-y-1">
              {m.attachments!.map((att, i) => {
                const href = downloadHref(att);
                const meta = [att.mime_type, formatBytes(att.size)].filter(Boolean).join(' · ');
                return (
                  <li key={att.id || i} className="flex items-center gap-2 text-sm text-content">
                    <span>📎</span>
                    {href ? (
                      <a href={href} download={att.filename || `attachment-${i}`} className="text-primary underline">
                        {att.filename || 'attachment'}
                      </a>
                    ) : (
                      <span>{att.filename || 'attachment'}</span>
                    )}
                    {meta && <span className="text-xs text-content-subtle">{meta}</span>}
                    {!href && <span className="text-xs italic text-content-subtle">— download unavailable</span>}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
